import { useEffect, useState } from "react";
import { toast } from "sonner";
import { teacherApi } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Database, Download, Trash2, AlertTriangle, Shield, Wrench, Gift, Sparkles, FileSearch, ArrowLeft, History, Mail, RefreshCw, TrendingUp, Eye, MessageSquareWarning, Check, Archive, MessageCircleReply, Send, X, Plus } from "lucide-react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Legend, CartesianGrid, LineChart, Line, PieChart, Pie, Cell } from "recharts";
import { Link } from "react-router-dom";
import { Section, KpiTile, SCOPE_LABELS } from "./_shared";
import { useConfirm } from "@/components/ConfirmDialog";

// ============================================================================
// INVITATIONS TAB
// ============================================================================
function InvitationsTab() {
  const confirm = useConfirm();
  const [rows, setRows] = useState([]);
  const [segments, setSegments] = useState({});
  const [segmentInput, setSegmentInput] = useState("");
  const [maxUses, setMaxUses] = useState("");
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(false);

  const load = async () => {
    try {
      const { data } = await teacherApi.get("/admin/invitations");
      setRows(data.invitations || []);
      setSegments(data.segments || {});
    } catch { toast.error("Nie udało się pobrać zaproszeń"); }
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    const seg = segmentInput.trim();
    if (!seg) { toast.error("Podaj segment (np. wave-1)"); return; }
    setLoading(true);
    try {
      const payload = { segment: seg, note: note.trim() || undefined };
      if (maxUses) payload.max_uses = parseInt(maxUses, 10);
      await teacherApi.post("/admin/invitations", payload);
      toast.success(`Zaproszenie „${seg}" utworzone`);
      setSegmentInput(""); setMaxUses(""); setNote("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Błąd tworzenia");
    } finally { setLoading(false); }
  };

  const revoke = async (id) => {
    const ok = await confirm({
      title: "Cofnąć zaproszenie?",
      description: "Link rejestracyjny przestanie działać. Istniejące rejestracje przez ten link zachowują swój tag segmentu.",
      confirmText: "Cofnij zaproszenie",
      variant: "danger",
    });
    if (!ok) return;
    try {
      await teacherApi.delete(`/admin/invitations/${id}`);
      toast.success("Zaproszenie cofnięte");
      load();
    } catch { toast.error("Nie udało się cofnąć"); }
  };

  const shareUrl = (code) => `${window.location.origin}/register?invite=${code}`;

  return (
    <Section icon={Gift} title="Zaproszenia (segmenty)">
      <p className="text-sm text-[#64748B] mb-4">
        Twórz brandowane linki rejestracyjne. Użytkownicy rejestrujący się przez
        dany link automatycznie dostają tag (segment), widoczny w
        <b> Statystyki → Segmenty</b>. Świetne dla wybranych szkół, wpływów
        marketingowych, wave-1 vs wave-2 itp.
      </p>

      {/* Create form */}
      <div className="brut border-2 border-[#0F172A] rounded-md bg-white p-4 mb-6" data-testid="invitation-create-form">
        <h3 className="font-black text-lg mb-3">Nowe zaproszenie</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <label className="text-xs font-black uppercase text-[#64748B]">Segment / tag *</label>
            <Input
              value={segmentInput} onChange={(e) => setSegmentInput(e.target.value)}
              placeholder="np. wave-1 lub liceum-warszawa"
              className="brut-input border-2 border-[#0F172A] rounded-md h-11 mt-1"
              maxLength={60}
              data-testid="invitation-segment-input"
            />
          </div>
          <div>
            <label className="text-xs font-black uppercase text-[#64748B]">Max użyć (opcjonalne)</label>
            <Input
              value={maxUses} onChange={(e) => setMaxUses(e.target.value)}
              type="number" min={1} max={10000}
              placeholder="bez limitu"
              className="brut-input border-2 border-[#0F172A] rounded-md h-11 mt-1"
              data-testid="invitation-maxuses-input"
            />
          </div>
          <div>
            <label className="text-xs font-black uppercase text-[#64748B]">Notatka (opcjonalne)</label>
            <Input
              value={note} onChange={(e) => setNote(e.target.value)}
              placeholder="widoczne dla rejestrującego"
              className="brut-input border-2 border-[#0F172A] rounded-md h-11 mt-1"
              maxLength={500}
              data-testid="invitation-note-input"
            />
          </div>
        </div>
        <Button
          onClick={create} disabled={loading || !segmentInput.trim()}
          className="mt-3 brut brut-hover bg-[#FEF08A] hover:bg-[#FEF08A] text-[#0F172A] border-2 border-[#0F172A] font-bold rounded-md"
          data-testid="invitation-create-btn"
        >
          <Plus className="w-4 h-4 mr-2" strokeWidth={2.5} />Utwórz zaproszenie
        </Button>
      </div>

      {/* Existing invitations list */}
      <div className="space-y-3" data-testid="invitations-list">
        {rows.length === 0 && (
          <div className="text-sm italic text-[#64748B]">
            Brak zaproszeń. Utwórz pierwsze powyżej.
          </div>
        )}
        {rows.map((r) => {
          const url = shareUrl(r.code);
          const used = segments[r.segment] || 0;
          const cap = r.max_uses ? ` / ${r.max_uses}` : "";
          return (
            <div
              key={r.id}
              className={`brut border-2 rounded-md p-4 bg-white ${r.revoked_at ? "border-[#94A3B8] opacity-70" : "border-[#0F172A]"}`}
              data-testid={`invitation-row-${r.id}`}
            >
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <code className="brut border-2 border-[#0F172A] bg-[#FEF08A] px-2 py-1 rounded-md font-black text-lg">
                      {r.code}
                    </code>
                    <span className="brut border-2 border-[#0F172A] bg-[#E0E7FF] px-2 py-0.5 text-xs font-black rounded">
                      {r.segment}
                    </span>
                    {r.revoked_at && (
                      <span className="brut border-2 border-[#94A3B8] bg-[#E2E8F0] px-2 py-0.5 text-[10px] font-black rounded">
                        COFNIĘTE
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-[#64748B] mt-2 break-all">
                    <b>Link:</b> {url}
                  </div>
                  {r.note && (
                    <div className="text-xs text-[#334155] mt-1 italic">„{r.note}&rdquo;</div>
                  )}
                  <div className="text-xs text-[#64748B] mt-1">
                    Utworzone: {new Date(r.created_at).toLocaleString("pl")} ·
                    Użyć: <b>{r.uses}{cap}</b> ·
                    Zarejestrowanych: <b>{used}</b>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    onClick={() => { navigator.clipboard.writeText(url); toast.success("Link skopiowany"); }}
                    className="brut brut-hover bg-white border-2 border-[#0F172A] font-bold rounded-md text-xs"
                    data-testid={`invitation-copy-${r.id}`}
                  >
                    Kopiuj link
                  </Button>
                  {!r.revoked_at && (
                    <Button
                      size="sm"
                      onClick={() => revoke(r.id)}
                      className="brut brut-hover bg-[#FCA5A5] border-2 border-[#0F172A] font-bold rounded-md text-xs"
                      data-testid={`invitation-revoke-${r.id}`}
                    >
                      <Trash2 className="w-3.5 h-3.5 mr-1" strokeWidth={2.5} />Cofnij
                    </Button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </Section>
  );
}


export default InvitationsTab;
